/**
 * Privacy Policy Page
 */

import React from 'react';
import { Shield, Lock, Eye, Trash2, Database, Share2 } from 'lucide-react';

const PRIVACY_SECTIONS = [
  {
    icon: Database,
    title: 'البيانات التي نجمعها',
    content: [
      'معلومات الحساب: الاسم، البريد الإلكتروني، رقم الجوال',
      'معلومات الملف الشخصي: العمر، المدينة، المنطقة، الوظيفة',
      'التفضيلات: إجابات الأسئلة لأغراض التوافق',
      'بيانات الاستخدام: وقت الدخول، التفاعلات داخل التطبيق'
    ]
  },
  {
    icon: Lock,
    title: 'كيف نحمي بياناتك',
    content: [
      'تشفير AES-256 للبيانات الحساسة (رقم الجوال، اسم العائلة)',
      'اتصال HTTPS آمن',
      'حماية من الاختراق وهجمات XSS',
      'خوادم آمنة تابعة لـ Google Firebase'
    ]
  },
  {
    icon: Eye,
    title: 'من يرى بياناتك',
    content: [
      'المعلومات الأساسية (الاسم الأول، المدينة، العمر) تظهر للتوافقات المحتملة',
      'رقم الجوال واسم العائلة لا يظهران إلا بموافقتك',
      'المحادثات مشفرة ولا نطّلع على محتواها',
      'لا نشارك بياناتك مع أي جهة خارجية'
    ]
  },
  {
    icon: Share2,
    title: 'مشاركة البيانات',
    content: [
      'لا نبيع بياناتك أبداً',
      'لا نشارك بياناتك مع جهات تسويقية',
      'قد نشارك بيانات مجمّعة (بدون هوية) لأغراض إحصائية',
      'قد نشارك البيانات إذا طُلب قانونياً من جهات حكومية'
    ]
  },
  {
    icon: Trash2,
    title: 'حذف البيانات',
    content: [
      'يحق لك طلب حذف حسابك في أي وقت',
      'عند الحذف، نزيل جميع بياناتك الشخصية',
      'يتم الحذف خلال 24 ساعة من الطلب',
      'بعض البيانات قد تُحفظ للمتطلبات القانونية'
    ]
  }
];

export default function Privacy() {
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-50 to-emerald-50 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <Shield className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            سياسة الخصوصية
          </h1>
          <p className="text-lg text-gray-600">
            خصوصيتك أولويتنا - آخر تحديث: يناير 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          {/* Intro */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-12">
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Lock className="w-6 h-6 text-green-600" />
              التزامنا بخصوصيتك
            </h2>
            <p className="text-gray-700 leading-relaxed">
              في "خالتي هيلة"، نأخذ خصوصيتك على محمل الجد. بياناتك مثل سرّك عند خالتك - محمية ومشفرة ولا يطّلع عليها أحد. نستخدم أحدث تقنيات الحماية لضمان أمان معلوماتك.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-12">
            {PRIVACY_SECTIONS.map((section, index) => {
              const Icon = section.icon;
              return (
                <div key={index}>
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <Icon className="w-5 h-5 text-green-600" />
                    </div>
                    {section.title}
                  </h2>
                  <ul className="space-y-3 pr-13">
                    {section.content.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-600">
                        <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Your Rights */}
          <div className="mt-12 bg-gray-50 rounded-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">حقوقك</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'الوصول لبياناتك الشخصية',
                'تصحيح البيانات غير الدقيقة',
                'طلب حذف بياناتك',
                'الاعتراض على معالجة بياناتك',
                'نقل بياناتك',
                'سحب موافقتك في أي وقت'
              ].map((right, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-700">
                  <Shield className="w-4 h-4 text-green-500" />
                  {right}
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-2">
              لأي استفسارات حول الخصوصية أو لممارسة حقوقك:
            </p>
            <a href="mailto:support@khaltihaila.com" className="text-green-600 font-medium hover:underline">
              support@khaltihaila.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
